import React from 'react'

import Image from 'shared/components/common/Image'
import ItemPrice from './ItemPrice'
import { Tooltip } from 'react-tooltip'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { use3pdConfig } from 'src/public/components/online_ordering/FulfillmentContext'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { t } from 'src/translations'
type FeeLineItemsProps = {
  loading: boolean
  diningOptionBehavior?: string | null
  deliveryFee?: number | null
  originalDeliveryFee?: number | null
  nonDeliveryNonGratuityNonUbpServiceCharges?: number
  gratuityServiceCharges?: number
  processingServiceCharges?: number
  tax?: number
}

export const FeeLineItems = ({
  loading,
  diningOptionBehavior,
  deliveryFee,
  originalDeliveryFee,
  nonDeliveryNonGratuityNonUbpServiceCharges,
  gratuityServiceCharges,
  processingServiceCharges,
  tax
}: FeeLineItemsProps) => {
  const { uses3pd } = use3pdConfig()
  const isIntlRx = useIsIntlRestaurant()
  const is3pdDelivery =
    uses3pd && diningOptionBehavior === DiningOptionBehavior.Delivery

  return (
    <>
      {diningOptionBehavior === DiningOptionBehavior.Delivery && (
        <FeeWithTooltip
          loading={loading}
          title={t('order-prices.delivery')}
          amount={deliveryFee ?? 0}
          originalAmount={originalDeliveryFee ?? undefined}
          tooltipTitle={t('order-prices.delivery.tooltip-title')}
          tooltipText={
            !is3pdDelivery || isIntlRx
              ? t('order-prices.delivery.tooltip-text.1pd')
              : t('order-prices.delivery.tooltip-text.3pd')
          }
          id='delivery'
        />
      )}
      {(nonDeliveryNonGratuityNonUbpServiceCharges ?? 0) > 0 && (
        <FeeWithTooltip
          loading={loading}
          title={t('order-prices.merchant-service-fees')}
          amount={nonDeliveryNonGratuityNonUbpServiceCharges ?? 0}
          tooltipTitle={t('order-prices.merchant-service-fees.tooltip-title')}
          tooltipText={t('order-prices.merchant-service-fees.tooltip-text')}
          id='merchant service fees'
        />
      )}
      {(gratuityServiceCharges ?? 0) > 0 && (
        <FeeWithTooltip
          loading={loading}
          title={t('order-prices.gratuities')}
          amount={gratuityServiceCharges ?? 0}
          tooltipTitle={t('order-prices.gratuities.tooltip-title')}
          tooltipText={t('order-prices.gratuities.tooltip-text')}
          id='gratuities'
        />
      )}
      {(processingServiceCharges ?? 0) > 0 && (
        <FeeWithTooltip
          loading={loading}
          title={t('order-prices.processing')}
          amount={processingServiceCharges ?? 0}
          tooltipTitle={t('order-prices.processing.tooltip-title')}
          tooltipText={t('order-prices.processing.tooltip-text')}
          id='processing'
        />
      )}
      {(tax ?? 0) > 0 && (
        <ItemPrice
          loading={loading}
          title={t('order-prices.tax')}
          amount={tax ?? 0}
        />
      )}
    </>
  )
}

export const FeeWithTooltip = ({
  title,
  amount,
  originalAmount,
  loading,
  tooltipText,
  tooltipTitle,
  id
}: {
  title: String
  amount: number
  originalAmount?: number
  loading: boolean
  tooltipText: string
  tooltipTitle: string
  id: string
}) => {
  const reactId = React.useId().replace(/:/g, '')
  const anchorId = `${id.replace(/\s+/g, '-')}-${reactId}-anchor`
  const tooltipId = `${anchorId}-tooltip`
  const titleComponent = (
    <div
      role='button'
      className='taxesAndFees'
      id={anchorId}
      tabIndex={0}
      aria-describedby={tooltipId}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.currentTarget.focus()
        }
      }}
    >
      <span>{title}</span>
      <div className='infoIconWrapper fullScreenMobile'>
        <Image src='icons/info.svg' className='infoIcon' alt='' />
      </div>
    </div>
  )

  return (
    <>
      <ItemPrice
        title={titleComponent}
        amount={amount}
        originalAmount={originalAmount}
        loading={loading}
        testId={`${title}-item-test-id`}
      />
      <Tooltip
        id={tooltipId}
        anchorSelect={`#${anchorId}`}
        className='feeTooltip'
      >
        <div className='tooltipTitle'>{tooltipTitle}</div>
        {tooltipText}
      </Tooltip>
    </>
  )
}
